import React, { useState, useEffect, useContext, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import { getStandardButtonStyles } from "../../styles/standardButtons";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import whatsappIcon from "../../assets/nopicture.png";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import useWhatsApps from "../../hooks/useWhatsApps";

import { Can } from "../Can";
import { Avatar, Grid, Input, Paper, Tab, Tabs, Switch, FormControlLabel, Typography, Tooltip, Box, IconButton } from "@material-ui/core";
import { getBackendUrl } from "../../config";
import TabPanel from "../TabPanel";
import AvatarUploader from "../AvatarUpload";

import ConfirmationNumberIcon from "@material-ui/icons/ConfirmationNumber";
import GroupIcon from "@material-ui/icons/Group";
import HistoryIcon from "@material-ui/icons/History";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import DashboardIcon from "@material-ui/icons/Dashboard";
import ContactsIcon from "@material-ui/icons/Contacts";
import TimelineIcon from "@material-ui/icons/Timeline";
import CastConnectedIcon from "@material-ui/icons/CastConnected";
import SendIcon from "@material-ui/icons/Send";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import ClearIcon from "@material-ui/icons/Clear";
import CloseIcon from "@material-ui/icons/Close";
import PersonIcon from "@material-ui/icons/Person";

const backendUrl = getBackendUrl();

const formatDateForInput = (date) => {
  if (!date) return '';

  // Se já está no formato YYYY-MM-DD, retorna como está
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  // Se é uma data ISO ou objeto Date, converte para YYYY-MM-DD
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';

  // Usar métodos locais para evitar problemas de timezone
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const parseDateFromInput = (dateString) => {
  if (!dateString) return null;

  // Se já está no formato YYYY-MM-DD, retorna como está
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  // Se é uma data ISO, extrai apenas a parte da data
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }

  return dateString;
};

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    borderRadius: "16px !important",
    border: `1px solid ${theme.palette.primary.main} !important`,
    boxShadow: theme.palette.type === "dark" 
      ? `0 0 20px ${theme.palette.primary.main}80 !important` 
      : `0 0 16px ${theme.palette.primary.main}40 !important`,
    backgroundColor: theme.palette.type === "dark" ? "#1E2126 !important" : "#FFFFFF !important",
    color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
    overflow: "hidden !important",
    display: "flex !important",
    flexDirection: "column !important",
    maxWidth: 780,
    width: "100%",
  },
  headerIcon: {
    marginRight: 10,
    fontSize: 24,
    color: theme.palette.primary.main,
  },
  dialogHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    "& h2": {
      display: "flex",
      alignItems: "center",
      fontSize: "1.15rem",
      fontWeight: 600,
      margin: 0,
      color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    gap: 8,
  },
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
  },
  avatar: {
    width: theme.spacing(12),
    height: theme.spacing(12),
    margin: theme.spacing(2),
    cursor: "pointer",
    borderRadius: "50%",
    border: "2px solid #ccc",
  },
  updateDiv: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  updateInput: {
    display: "none",
  },
  updateLabel: {
    padding: theme.spacing(1),
    margin: theme.spacing(1),
    textTransform: "uppercase",
    textAlign: "center",
    cursor: "pointer",
    border: "2px solid #ccc",
    borderRadius: "5px",
    minWidth: 160,
    fontWeight: "bold",
    color: "#555",
  },
  errorUpdate: {
    border: "2px solid red",
  },
  errorText: {
    color: "red",
    fontSize: "0.8rem",
    fontWeight: "bold",
  },
  customScrollbar: {
    "&::-webkit-scrollbar": {
      width: "8px",
      height: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.type === "light" 
        ? `${theme.palette.primary.main}80` 
        : `${theme.palette.primary.main}66`,
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: theme.palette.primary.main,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
      margin: "8px 0",
    },
  },
  permCard: {
    display: "flex",
    alignItems: "center",
    padding: "10px 14px",
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.03)" : "#F8FAFC",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.07)" : "1px solid #E2E8F0",
    transition: "all 0.2s ease",
    cursor: "pointer",
    userSelect: "none",
    height: "100%",
    boxSizing: "border-box",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.06)" : "#F1F5F9",
      borderColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.18)" : "#CBD5E1",
      transform: "translateY(-1px)",
    },
  },
  permCardActiveAccount: {
    border: "1px solid rgba(46, 213, 115, 0.4)",
    backgroundColor: "rgba(46, 213, 115, 0.04)",
    "&:hover": {
      backgroundColor: "rgba(46, 213, 115, 0.08)",
      borderColor: "rgba(46, 213, 115, 0.6)",
    },
  },
  permCardChecked: {
    border: "1px solid rgba(0, 168, 255, 0.35)",
    backgroundColor: "rgba(0, 168, 255, 0.05)",
    "&:hover": {
      backgroundColor: "rgba(0, 168, 255, 0.09)",
      borderColor: "rgba(0, 168, 255, 0.55)",
    },
  },
  permIconWrap: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
    flexShrink: 0,
  },
  permTextWrap: {
    flex: 1,
    minWidth: 0,
    marginRight: 8,
  },
  permTitle: {
    fontSize: "0.82rem",
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#F1F2F6" : "#1E293B",
    lineHeight: 1.25,
    marginBottom: 2,
  },
  permDesc: {
    fontSize: "0.7rem",
    color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.55)" : "#64748B",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  permSwitchWrap: {
    flexShrink: 0,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    margin: "14px 0 8px 0",
    paddingBottom: 4,
    borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.06)" : "1px solid #E2E8F0",
  },
  sectionTitle: {
    fontSize: "0.75rem",
    fontWeight: 700,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.7)" : "#475569",
  },
  topActionsBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
    paddingBottom: 8,
    borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
  },
  quickBtn: {
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.75rem",
    borderRadius: 6,
    padding: "3px 10px",
  },
}));

const PERMISSION_GROUPS = [
  {
    key: "tickets",
    titleKey: "userModal.permissions.sections.tickets",
    items: [
      {
        field: "allTicket",
        titleKey: "userModal.permissions.allTicket.title",
        descKey: "userModal.permissions.allTicket.desc",
        icon: <ConfirmationNumberIcon fontSize="small" />,
        color: "#00a8ff"
      },
      {
        field: "allowGroup",
        titleKey: "userModal.permissions.allowGroup.title",
        descKey: "userModal.permissions.allowGroup.desc",
        icon: <GroupIcon fontSize="small" />,
        color: "#2ed573"
      },
      {
        field: "allHistoric",
        titleKey: "userModal.permissions.allHistoric.title",
        descKey: "userModal.permissions.allHistoric.desc",
        icon: <HistoryIcon fontSize="small" />,
        color: "#ffa502"
      },
      {
        field: "allUserChat",
        titleKey: "userModal.permissions.allUserChat.title",
        descKey: "userModal.permissions.allUserChat.desc",
        icon: <SupervisorAccountIcon fontSize="small" />,
        color: "#9b59b6"
      },
      {
        field: "allowSeeMessagesInPendingTickets",
        titleKey: "userModal.permissions.allowSeeMessagesInPendingTickets.title",
        descKey: "userModal.permissions.allowSeeMessagesInPendingTickets.desc",
        icon: <VisibilityIcon fontSize="small" />,
        color: "#1abc9c"
      },
      {
        field: "userClosePendingTicket",
        titleKey: "userModal.permissions.userClosePendingTicket.title",
        descKey: "userModal.permissions.userClosePendingTicket.desc",
        icon: <CheckCircleOutlineIcon fontSize="small" />,
        color: "#e67e22"
      }
    ]
  },
  {
    key: "modules",
    titleKey: "userModal.permissions.sections.modules",
    items: [
      {
        field: "showDashboard",
        titleKey: "userModal.permissions.showDashboard.title",
        descKey: "userModal.permissions.showDashboard.desc",
        icon: <DashboardIcon fontSize="small" />,
        color: "#3498db"
      },
      {
        field: "showContacts",
        titleKey: "userModal.permissions.showContacts.title",
        descKey: "userModal.permissions.showContacts.desc",
        icon: <ContactsIcon fontSize="small" />,
        color: "#2ecc71"
      },
      {
        field: "allowRealTime",
        titleKey: "userModal.permissions.allowRealTime.title",
        descKey: "userModal.permissions.allowRealTime.desc",
        icon: <TimelineIcon fontSize="small" />,
        color: "#e74c3c"
      },
      {
        field: "allowConnections",
        titleKey: "userModal.permissions.allowConnections.title",
        descKey: "userModal.permissions.allowConnections.desc",
        icon: <CastConnectedIcon fontSize="small" />,
        color: "#f39c12"
      },
      {
        field: "showCampaign",
        titleKey: "userModal.permissions.showCampaign.title",
        descKey: "userModal.permissions.showCampaign.desc",
        icon: <SendIcon fontSize="small" />,
        color: "#9b59b6"
      },
      {
        field: "showFlow",
        titleKey: "userModal.permissions.showFlow.title",
        descKey: "userModal.permissions.showFlow.desc",
        icon: <AccountTreeIcon fontSize="small" />,
        color: "#16a085"
      }
    ]
  },
  {
    key: "account",
    titleKey: "userModal.permissions.sections.account",
    items: [
      {
        field: "finalizacaoComValorVendaAtiva",
        titleKey: "userModal.permissions.finalizacaoComValorVendaAtiva.title",
        descKey: "userModal.permissions.finalizacaoComValorVendaAtiva.desc",
        icon: <MonetizationOnIcon fontSize="small" />,
        color: "#f1c40f"
      },
      {
        field: "active",
        titleKey: "userModal.permissions.active.title",
        descKey: "userModal.permissions.active.desc",
        icon: <CheckCircleIcon fontSize="small" />,
        color: "#2ecc71",
        isSpecial: true
      }
    ]
  }
];

const getPermissionValue = (field, values) => {
  const val = values[field];
  if (field === "allTicket") return val === "enable" || val === "enabled";
  if (field === "allowGroup" || field === "active") return Boolean(val);
  if (field === "finalizacaoComValorVendaAtiva") return String(val) === "true";
  return val === "enabled" || val === "enable" || val === true;
};

const setPermissionValue = (field, nextBool, setFieldValue) => {
  if (field === "allTicket") {
    setFieldValue(field, nextBool ? "enable" : "disable");
  } else if (field === "allowGroup" || field === "active") {
    setFieldValue(field, nextBool);
  } else if (field === "finalizacaoComValorVendaAtiva") {
    setFieldValue(field, nextBool ? "true" : "false");
  } else {
    setFieldValue(field, nextBool ? "enabled" : "disabled");
  }
};

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("userModal.validations.tooShort"))
    .max(50, i18n.t("userModal.validations.tooLong"))
    .required(i18n.t("userModal.validations.required")),
  password: Yup.string()
    .min(5, i18n.t("userModal.validations.tooShort"))
    .max(50, i18n.t("userModal.validations.tooLong")),
  email: Yup.string()
    .email(i18n.t("userModal.validations.invalidEmail"))
    .required(i18n.t("userModal.validations.required")),
  allHistoric: Yup.string().nullable(),
});


const UserModal = ({ open, onClose, userId }) => {
  const classes = useStyles();

  const initialState = {
    name: "",
    email: "",
    password: "",
    birthDate: "",
    profile: "user",
    startWork: "00:00",
    endWork: "23:59",
    farewellMessage: "",
    allTicket: "enable",
    allowGroup: false,
    defaultTheme: "light",
    defaultMenu: "open",
    allHistoric: "enabled",
    allUserChat: "enabled",
    userClosePendingTicket: "enabled",
    showDashboard: "enabled",
    allowRealTime: "enabled",
    allowConnections: "enabled",
    showContacts: "enabled",
    showCampaign: "enabled",
    showFlow: "enabled",
    finalizacaoComValorVendaAtiva: "true",
    allowSeeMessagesInPendingTickets: "enabled",
    active: true,
    resellerBalance: 0,
    discountPercentage: 0
  };

  const { user: loggedInUser, setUser: setAuthUser } = useContext(AuthContext);

  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [whatsappId, setWhatsappId] = useState("");
  const [whatsappIdInitialized, setWhatsappIdInitialized] = useState(false);
  // const [allTicket, setAllTicket] = useState("disable");
  const { loading, whatsApps } = useWhatsApps();
  const [profileUrl, setProfileUrl] = useState(null);
  const [tab, setTab] = useState("general");
  const [avatar, setAvatar] = useState(null);
  const startWorkRef = useRef();
  const endWorkRef = useRef();

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser((prevState) => ({
          ...prevState,
          ...data,
          allTicket:
            data.allTicket === "enable" || data.allTicket === "enabled"
              ? "enable"
              : "disable",
          finalizacaoComValorVendaAtiva: data.finalizacaoComValorVendaAtiva
            ? "true"
            : "false",
          allowSeeMessagesInPendingTickets: data.allowSeeMessagesInPendingTickets === "enabled" ? "enabled" : "disabled",
          farewellMessage: data.farewellMessage || "",
          // Formatar a data corretamente
          birthDate: formatDateForInput(data.birthDate),
          active: data.active !== false
        }));

        const { profileImage } = data;
        if (profileImage && profileImage.startsWith("default/")) {
          setProfileUrl(`${backendUrl}/public/${profileImage}`);
        } else {
          setProfileUrl(
            `${backendUrl}/public/company${data.companyId}/user/${profileImage}`
          );
        }

        const userQueueIds = data.queues?.map((queue) => queue.id) || [];
        setSelectedQueueIds(userQueueIds);
        setWhatsappId(data.whatsappId ? data.whatsappId : "");
        setWhatsappIdInitialized(true);
      } catch (err) {
        toastError(err);
      }
    };

    fetchUser();
  }, [userId, open]);

  const handleClose = () => {
    onClose();
    setUser(initialState);
    setWhatsappId("");
    setSelectedQueueIds([]);
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const getBasename = (filepath) => {
    if (!filepath) return '';
    // Remove query strings e hashes
    const cleanPath = filepath.split('?')[0].split('#')[0];
    // Pega o último segmento após /
    const segments = cleanPath.split('/');
    return segments[segments.length - 1];
  };

  // UserModal/index.js - Função handleSaveUser corrigida
  const handleSaveUser = async (values) => {
    const uploadAvatar = async (userId) => {
      if (!avatar || typeof avatar !== 'object') return null;

      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("typeArch", "user");
      formData.append("profileImage", avatar);

      try {
        const { data } = await api.post(
          `/users/${userId}/media-upload?typeArch=user`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
        return data.user.profileImage;
      } catch (error) {
        console.error("Erro no upload da imagem:", error);
        throw error;
      }
    };

    const userData = {
      ...values,
      whatsappId,
      queueIds: selectedQueueIds,
      finalizacaoComValorVendaAtiva: values.finalizacaoComValorVendaAtiva === "true",
      birthDate: parseDateFromInput(values.birthDate),
      allowSeeMessagesInPendingTickets: values.allowSeeMessagesInPendingTickets === "enabled" ? "enabled" : "disabled",
      active: values.active
    };

    try {
      let responseData;

      if (userId) {
        // Atualizar usuário existente
        const { data } = await api.put(`/users/${userId}`, userData);
        responseData = data;

        // Upload da imagem se houver uma nova
        if (avatar && typeof avatar === 'object') {
          const newProfileImage = await uploadAvatar(userId);
          if (newProfileImage) {
            responseData.profileImage = newProfileImage;

            // Atualizar localStorage se for o usuário logado
            if (userId === loggedInUser.id) {
              localStorage.setItem("profileImage", newProfileImage);
            }
          }
        }
      } else {
        // Criar novo usuário
        const { data } = await api.post("/users", userData);
        responseData = data.user;

        // Upload da imagem se houver
        if (avatar && typeof avatar === 'object' && responseData.id) {
          const newProfileImage = await uploadAvatar(responseData.id);
          if (newProfileImage) {
            responseData.profileImage = newProfileImage;
          }
        }
      }

      handleClose();
      toast.success(i18n.t("userModal.success"));

      // Atualizar interface sem recarregar
      if (userId === loggedInUser.id) {
        setAuthUser({ ...loggedInUser, ...responseData });
      }
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <div className={classes.dialogHeader}>
        <h2>
          <PersonIcon className={classes.headerIcon} />
          {userId
            ? i18n.t("userModal.title.edit")
            : i18n.t("userModal.title.add")}
        </h2>
        <Box display="flex" alignItems="center">
          <Typography variant="body2" style={{ color: "gray", marginRight: 8 }}>
            BotMix | {i18n.t("userModal.title.subHeader") || "Usuario"}
          </Typography>
          <IconButton size="small" onClick={handleClose} style={{ color: "inherit" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </div>
        <Formik
          initialValues={user}
          enableReinitialize={true}
          validationSchema={UserSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveUser(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values }) => (
            <Form style={{ display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 120px)", overflow: "hidden" }}>
                <Tabs
                  value={tab}
                  indicatorColor="primary"
                  textColor="primary"
                  scrollButtons="on"
                  variant="scrollable"
                  onChange={handleTabChange}
                  className={classes.tab}
                >
                  <Tab
                    label={i18n.t("userModal.tabs.general")}
                    value={"general"}
                  />
                  <Tab
                    label={i18n.t("userModal.tabs.permissions")}
                    value={"permissions"}
                  />
                </Tabs>
              <DialogContent dividers className={classes.customScrollbar}>
                  <TabPanel
                    className={classes.container}
                    value={tab}
                    name={"general"}
                  >
                    <AvatarUploader
                      setAvatar={setAvatar}
                      avatar={user.profileImage}
                      companyId={user.companyId}
                      onRemoveAvatar={() => {
                        user.profileImage = null;
                        setFieldValue("profileImage", null);
                        setAvatar(null);
                      }}
                    />
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6} xl={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("userModal.form.name")}
                          autoFocus
                          name="name"
                          error={touched.name && Boolean(errors.name)}
                          helperText={touched.name && errors.name}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={6} xl={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("userModal.form.password")}
                          type="password"
                          name="password"
                          error={touched.password && Boolean(errors.password)}
                          helperText={touched.password && errors.password}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={8} xl={8}>
                        <Field
                          as={TextField}
                          label={i18n.t("userModal.form.email")}
                          name="email"
                          error={touched.email && Boolean(errors.email)}
                          helperText={touched.email && errors.email}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={12} md={4} xl={4}>
                        <FormControl
                          variant="outlined"
                          //className={classes.formControl}
                          margin="dense"
                          fullWidth
                        >
                          <Can
                            role={loggedInUser.profile}
                            perform="user-modal:editProfile"
                            yes={() => (
                              <>
                                <InputLabel id="profile-selection-input-label">
                                  {i18n.t("userModal.form.profile")}
                                </InputLabel>

                                <Field
                                  as={Select}
                                  label={i18n.t("userModal.form.profile")}
                                  name="profile"
                                  labelId="profile-selection-label"
                                  id="profile-selection"
                                  required
                                >
                                  <MenuItem value="admin">
                                    {i18n.t("userModal.form.profileAdmin")}
                                  </MenuItem>
                                  <MenuItem value="user">
                                    {i18n.t("userModal.form.profileUser")}
                                  </MenuItem>
                                </Field>
                              </>
                            )}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={12} xl={12}>
                        <Can
                          role={loggedInUser.profile}
                          perform="user-modal:editQueues"
                          yes={() => (
                            <QueueSelect
                              selectedQueueIds={selectedQueueIds}
                              onChange={(values) => setSelectedQueueIds(values)}
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={12} xl={12}>
                        <Can
                          role={loggedInUser.profile}
                          perform="user-modal:editProfile"
                          yes={() => (
                            <FormControl
                              variant="outlined"
                              margin="dense"
                              className={classes.maxWidth}
                              fullWidth
                            >
                              <InputLabel>
                                {i18n.t("userModal.form.whatsapp")}
                              </InputLabel>
                              <Field
                                as={Select}
                                value={whatsappId}
                                onChange={(e) => setWhatsappId(e.target.value)}
                                label={i18n.t("userModal.form.whatsapp")}
                              >
                                <MenuItem value={""}>&nbsp;</MenuItem>
                                {whatsApps.map((whatsapp) => (
                                  <MenuItem
                                    key={whatsapp.id}
                                    value={whatsapp.id}
                                  >
                                    {whatsapp.name}
                                  </MenuItem>
                                ))}
                              </Field>
                            </FormControl>
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <Grid container spacing={1}>
                          <Grid item xs={12} md={6} xl={6}>
                            <Field
                              as={TextField}
                              label={i18n.t("userModal.form.startWork")}
                              type="time"
                              ampm={"false"}
                              inputRef={startWorkRef}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              inputProps={{
                                step: 600, // 5 min
                              }}
                              fullWidth
                              name="startWork"
                              error={
                                touched.startWork && Boolean(errors.startWork)
                              }
                              helperText={touched.startWork && errors.startWork}
                              variant="outlined"
                              margin="dense"
                              className={classes.textField}
                            />
                          </Grid>
                          <Grid item xs={12} md={6} xl={6}>
                            <Field
                              as={TextField}
                              label={i18n.t("userModal.form.endWork")}
                              type="time"
                              ampm={"false"}
                              inputRef={endWorkRef}
                              InputLabelProps={{
                                shrink: true,
                              }}
                              inputProps={{
                                step: 600, // 5 min
                              }}
                              fullWidth
                              name="endWork"
                              error={touched.endWork && Boolean(errors.endWork)}
                              helperText={touched.endWork && errors.endWork}
                              variant="outlined"
                              margin="dense"
                              className={classes.textField}
                            />
                          </Grid>
                        </Grid>
                      )}
                    />

                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6} xl={6}>
                        <Field
                          as={TextField}
                          label={i18n.t("userModal.form.birthDate")}
                          type="date"
                          name="birthDate"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          fullWidth
                          variant="outlined"
                          margin="dense"
                          className={classes.textField}
                          helperText={i18n.t("userModal.form.birthDateHelper")}
                          onChange={(e) => {
                            const formattedDate = parseDateFromInput(e.target.value);
                            setFieldValue('birthDate', formattedDate);
                          }}
                        />
                      </Grid>
                    </Grid>

                    <Field
                      as={TextField}
                      label={i18n.t("userModal.form.farewellMessage")}
                      type="text"
                      multiline
                      rows={4}
                      fullWidth
                      name="farewellMessage"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      error={
                        touched.farewellMessage &&
                        Boolean(errors.farewellMessage)
                      }
                      helperText={
                        touched.farewellMessage && errors.farewellMessage
                      }
                      variant="outlined"
                      margin="dense"
                    />

                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6} xl={6}>
                        <FormControl
                          variant="outlined"
                          className={classes.maxWidth}
                          margin="dense"
                          fullWidth
                        >
                          <>
                            <InputLabel>
                              {i18n.t("userModal.form.defaultTheme")}
                            </InputLabel>

                            <Field
                              as={Select}
                              label={i18n.t("userModal.form.defaultTheme")}
                              name="defaultTheme"
                              type="defaultTheme"
                              required
                            >
                              <MenuItem value="light">
                                {i18n.t("userModal.form.defaultThemeLight")}
                              </MenuItem>
                              <MenuItem value="dark">
                                {i18n.t("userModal.form.defaultThemeDark")}
                              </MenuItem>
                            </Field>
                          </>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6} xl={6}>
                        <FormControl
                          variant="outlined"
                          className={classes.maxWidth}
                          margin="dense"
                          fullWidth
                        >
                          <>
                            <InputLabel>
                              {i18n.t("userModal.form.defaultMenu")}
                            </InputLabel>

                            <Field
                              as={Select}
                              label={i18n.t("userModal.form.defaultMenu")}
                              name="defaultMenu"
                              type="defaultMenu"
                              required
                            >
                              <MenuItem value={"open"}>
                                {i18n.t("userModal.form.defaultMenuOpen")}
                              </MenuItem>
                              <MenuItem value={"closed"}>
                                {i18n.t("userModal.form.defaultMenuClosed")}
                              </MenuItem>
                            </Field>
                          </>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </TabPanel>
                  <TabPanel
                    className={classes.container}
                    value={tab}
                    name={"permissions"}
                  >
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <div style={{ width: "100%", padding: "4px 0" }}>
                          {/* Barra Superior con Acciones Rápidas */}
                          <div className={classes.topActionsBar}>
                            <Typography
                              variant="body2"
                              style={{ color: "rgba(255, 255, 255, 0.7)", fontWeight: 600 }}
                            >
                              {i18n.t("userModal.tabs.permissions")}
                            </Typography>
                            <div style={{ display: "flex", gap: 8 }}>
                              <Button
                                type="button"
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<DoneAllIcon />}
                                className={classes.quickBtn}
                                onClick={() => {
                                  const allFields = [
                                    "allTicket",
                                    "allowGroup",
                                    "allHistoric",
                                    "allUserChat",
                                    "allowSeeMessagesInPendingTickets",
                                    "userClosePendingTicket",
                                    "showDashboard",
                                    "showContacts",
                                    "allowRealTime",
                                    "allowConnections",
                                    "showCampaign",
                                    "showFlow",
                                    "finalizacaoComValorVendaAtiva"
                                  ];
                                  allFields.forEach((f) => setPermissionValue(f, true, setFieldValue));
                                }}
                              >
                                {i18n.t("userModal.permissions.quickActions.enableAll")}
                              </Button>
                              <Button
                                type="button"
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<ClearIcon />}
                                className={classes.quickBtn}
                                onClick={() => {
                                  const allFields = [
                                    "allTicket",
                                    "allowGroup",
                                    "allHistoric",
                                    "allUserChat",
                                    "allowSeeMessagesInPendingTickets",
                                    "userClosePendingTicket",
                                    "showDashboard",
                                    "showContacts",
                                    "allowRealTime",
                                    "allowConnections",
                                    "showCampaign",
                                    "showFlow",
                                    "finalizacaoComValorVendaAtiva"
                                  ];
                                  allFields.forEach((f) => setPermissionValue(f, false, setFieldValue));
                                }}
                              >
                                {i18n.t("userModal.permissions.quickActions.disableAll")}
                              </Button>
                            </div>
                          </div>

                          {/* Grupos de Permisos */}
                          {PERMISSION_GROUPS.map((group) => (
                            <div key={group.key} style={{ marginBottom: 16 }}>
                              <div className={classes.sectionHeader}>
                                <Typography className={classes.sectionTitle}>
                                  {i18n.t(group.titleKey)}
                                </Typography>
                              </div>
                              <Grid container spacing={2}>
                                {group.items.map((perm) => {
                                  const isChecked = getPermissionValue(perm.field, values);
                                  const isDisabled = perm.field === "active" && +userId === loggedInUser.id;
                                  return (
                                    <Grid item xs={12} sm={6} key={perm.field}>
                                      <Paper
                                        elevation={0}
                                        className={`${classes.permCard} ${
                                          perm.isSpecial && isChecked
                                            ? classes.permCardActiveAccount
                                            : isChecked
                                            ? classes.permCardChecked
                                            : ""
                                        }`}
                                        onClick={() => {
                                          if (!isDisabled) {
                                            setPermissionValue(perm.field, !isChecked, setFieldValue);
                                          }
                                        }}
                                      >
                                        <div
                                          className={classes.permIconWrap}
                                          style={{
                                            color: perm.color,
                                            backgroundColor: `${perm.color}18`
                                          }}
                                        >
                                          {perm.icon}
                                        </div>
                                        <div className={classes.permTextWrap}>
                                          <Typography className={classes.permTitle}>
                                            {i18n.t(perm.titleKey)}
                                          </Typography>
                                          <Tooltip title={i18n.t(perm.descKey)} enterDelay={500} arrow>
                                            <Typography className={classes.permDesc}>
                                              {i18n.t(perm.descKey)}
                                            </Typography>
                                          </Tooltip>
                                        </div>
                                        <div className={classes.permSwitchWrap}>
                                          <Switch
                                            checked={isChecked}
                                            disabled={isDisabled}
                                            color="primary"
                                            onChange={(e) => {
                                              setPermissionValue(perm.field, e.target.checked, setFieldValue);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          />
                                        </div>
                                      </Paper>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </div>
                          ))}
                        </div>
                      )}
                    />
                  </TabPanel>
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className={classes.secondaryButton}
                >
                  {i18n.t("userModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className={classes.applyButton}
                >
                  {isSubmitting ? (
                    <CircularProgress
                      size={20}
                      className={classes.buttonProgress}
                    />
                  ) : (
                    userId
                      ? i18n.t("userModal.buttons.okEdit")
                      : i18n.t("userModal.buttons.okAdd")
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default UserModal;
